import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import type { RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type EditUserDetailsPayload } from '@webapp/platform/iam/data-access/types/editUserDetailsPayload'
import { type UpdateProfileImagePayload } from '@webapp/platform/iam/data-access/types/updateProfileImagePayload'
import { type UpdatePurchasePriceLimitPayload } from '@webapp/platform/iam/data-access/types/updatePurchasePriceLimitPayload'
import type { DetailedUserConstructor } from '@webapp/platform/iam/entities/DetailedUser'
import { type UserPreferenceKey } from '@webapp/platform/iam/entities/enums/UserPreferenceKeys'
import { type UserConstructor } from '@webapp/platform/iam/entities/User'
import { type UserCountByTypeConstructor } from '@webapp/platform/iam/entities/UserCountByType'
import { type UserPurchasePriceLimitConstructor } from '@webapp/platform/iam/entities/UserPurchasePriceLimit/UserPurchasePriceLimit'
import { type UserSummaryConstructor } from '@webapp/platform/iam/entities/UserSummary'

/**
 * Coded 400s on `legal_entities` from PUT /api/v3/users/{id}/ and
 * POST /api/v3/users/ when Multi-Entity is on (PLAT-5016).
 */
export const USER_LEGAL_ENTITIES_REQUIRED = 'USER_LEGAL_ENTITIES_REQUIRED'
export const INACTIVE_LEGAL_ENTITY = 'INACTIVE_LEGAL_ENTITY'

export interface IUserRepository {
  getPreference(
    userId: number,
    key: UserPreferenceKey
  ): Promise<Nullable<string>>

  setPreference(userId: number, key: UserPreferenceKey, value: string): void

  list(
    isActive?: boolean,
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<UserConstructor>>

  getDetails(
    userId: number | uuid
  ): Promise<RepoStandardResponse<DetailedUserConstructor>>

  getBasicUserById(
    userId: number
  ): Promise<RepoStandardResponse<UserSummaryConstructor>>

  getCounts(): Promise<RepoStandardResponse<UserCountByTypeConstructor>>

  editDetails(userId: number, payload: EditUserDetailsPayload): Promise<void>

  getPurchasePriceLimit(
    userId: number
  ): Promise<RepoStandardResponse<UserPurchasePriceLimitConstructor>>

  updatePurchasePriceLimit(
    userId: number,
    payload: UpdatePurchasePriceLimitPayload
  ): Promise<void>

  resetUserMFA(userId?: number): Promise<void>

  delete(userId: number): Promise<void>

  activate(userId: number): Promise<void>

  updateProfileImage(
    userId: number,
    payload: UpdateProfileImagePayload
  ): Promise<RepoStandardResponse<UserConstructor>>
}
